import { getPackageLimit } from "./packageLimits";
import type {
  Department,
  DepartmentResponse,
  DepartmentStatus,
  PackageType,
  ResponseStatus,
  WorkshopAnswers
} from "../types";

export function startDepartment(department: Department): Department {
  if (department.status !== "invited") {
    return department;
  }

  return {
    ...department,
    status: "started"
  };
}

export function submitDepartment(department: Department): Department {
  return {
    ...department,
    status: "submitted"
  };
}

export function canCreateDepartment(currentCount: number, packageType: PackageType): boolean {
  return currentCount < getPackageLimit(packageType).maxDepartments;
}

export function getSubmittedDepartmentCount(departments: Department[]): number {
  return departments.filter((department) => department.status === "submitted" || department.status === "reviewed").length;
}

export function getSubmittedResponses<T extends { responseStatus: "draft" | "submitted" }>(responses: T[]): T[] {
  return responses.filter((response) => response.responseStatus === "submitted");
}

export function saveDraftResponse(params: {
  existingResponse?: DepartmentResponse;
  workspaceId: string;
  departmentId: string;
  userId?: string;
  answers: WorkshopAnswers;
  now?: string;
}): DepartmentResponse {
  const now = params.now ?? new Date().toISOString();

  if (params.existingResponse?.responseStatus === "submitted") {
    return params.existingResponse;
  }

  return {
    id: params.existingResponse?.id ?? `response_${params.departmentId}`,
    workspaceId: params.workspaceId,
    departmentId: params.departmentId,
    userId: params.userId,
    responseStatus: "draft",
    answers: params.answers,
    submittedAt: undefined,
    createdAt: params.existingResponse?.createdAt ?? now,
    updatedAt: now
  };
}

export function submitResponse(response: DepartmentResponse, now = new Date().toISOString()): DepartmentResponse {
  return {
    ...response,
    responseStatus: "submitted",
    submittedAt: response.submittedAt ?? now,
    updatedAt: now
  };
}

export function statusLabel(status: DepartmentStatus | ResponseStatus, language: "de" | "en" = "de"): string {
  const labels: Record<"de" | "en", Record<DepartmentStatus | ResponseStatus, string>> = {
    de: {
      invited: "Eingeladen",
      started: "In Bearbeitung",
      submitted: "Eingereicht",
      reviewed: "Geprüft",
      draft: "Entwurf"
    },
    en: {
      invited: "Invited",
      started: "In progress",
      submitted: "Submitted",
      reviewed: "Reviewed",
      draft: "Draft"
    }
  };

  return labels[language][status];
}
